import React, { useState, useMemo } from "react";
import Modal from "../common/Modal";

// Categorised icons. Keep categories focused; duplicates will be removed at render-time.
const ICONS_BY_CATEGORY = {
  People: [
    "😀",
    "🧑‍🚀",
    "🧑‍🎤",
    "🧑‍🔬",
    "🧑‍🌾",
    "🧑‍🏫",
    "🧑‍💻",
    "🧑‍🍳",
    "🧑‍🎨",
    "🧑‍✈️",
    "🧑‍🚒",
    "🤖",
    "🧙",
  ],
  Animals: [
    "🐱",
    "🐶",
    "🐼",
    "🐧",
    "🐸",
    "🦉",
    "🦁",
    "🐯",
    "🐵",
    "🦋",
    "🐢",
    "🐙",
    "🦖",
    "🦕",
    "🐝",
    "🦀",
    "🐬",
    "🦥",
    "🦩",
  ],
  Food: ["🍕", "🍔", "🍩", "☕", "🍺", "🍎", "🍉", "🍣", "🍪", "🍜"],
  Sports: ["🏀", "⚽", "🏄", "🏖️", "🚴", "🏓", "🏆", "🏅", "🎯"],
  Nature: [
    "🌟",
    "🔥",
    "⚡",
    "🌙",
    "☀️",
    "⛈️",
    "🌲",
    "🌵",
    "🍀",
    "🌺",
    "🌻",
    "🍂",
    "❄️",
    "🌈",
    "🌊",
  ],
  Objects: [
    "💎",
    "🎩",
    "🎻",
    "🎺",
    "🥁",
    "🪐",
    "🔔",
    "🔑",
    "🛡️",
    "⚔️",
    "🎬",
    "🕰️",
    "📷",
  ],
  Games: ["🎮", "🎲", "♟️", "🃏", "🎴", "🀄", "🕹️", "🧩", "🧸"],
  Symbols: ["❤️", "💚", "💙", "💜", "🖤", "💛", "✨", "🎉", "🎁", "🔮", "🧿"],
  Tech: ["💻", "📱", "🖥️", "⌨️", "🖱️", "🎧"],
  Transport: ["🚗", "✈️", "🚲", "🚂", "🛶", "⛵", "🚁", "🚀"],
};

const CATEGORY_ORDER = ["All", ...Object.keys(ICONS_BY_CATEGORY)];

// Helper to validate emoji (simple unicode test)
function isValidEmoji(str) {
  if (!str || typeof str !== 'string' || !str.trim()) return false;
  return /\p{Emoji}/u.test(str);
}

function unique(arr) {
  const seen = new Set();
  const out = [];
  for (const a of arr) {
    if (!seen.has(a)) {
      seen.add(a);
      out.push(a);
    }
  }
  return out;
}

function flattenAllIcons() {
  return unique(Object.values(ICONS_BY_CATEGORY).flat());
}

function UserIconPickerModal({ open, onClose, onSelect, selectedIcon }) {
  const [category, setCategory] = useState("All");

  const filteredIcons = useMemo(() => {
    if (category === "All") return flattenAllIcons();
    const list = ICONS_BY_CATEGORY[category] || [];
    return unique(list);
  }, [category]);

  return (
    <Modal show={open} title="Pick an Icon" onClose={onClose}>
      <div
        style={{
          maxHeight: "calc(80vh - 3cm)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div className="p-4 max-w-md mx-auto">
          <div className="mb-3 flex gap-2 flex-wrap justify-center">
            {CATEGORY_ORDER.map((cat) => (
              <button
                key={cat}
                type="button"
                className={`px-2 py-1 rounded text-sm ${
                  category === cat
                    ? "bg-blue-500 text-white"
                    : "bg-gray-100 dark:bg-gray-700"
                }`}
                onClick={() => setCategory(cat)}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
        <div
          className="p-4 max-w-md mx-auto"
          style={{ height: "70vh", maxHeight: "70vh", overflowY: "auto" }}
        >
          <div className="flex flex-wrap gap-2 justify-center">
            {filteredIcons.filter(isValidEmoji).map((ic) => (
              <button
                type="button"
                key={ic}
                className={`text-4xl p-1 rounded ${
                  selectedIcon === ic ? "ring-2 ring-blue-500" : ""
                }`}
                onClick={() => {
                  onSelect(ic);
                  onClose();
                }}
                aria-label={ic}
              >
                {ic}
              </button>
            ))}
          </div>
        </div>
      </div>
    </Modal>
  );
}



export default UserIconPickerModal;
